import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ArrowRight, Users, Trees, Sparkles, WifiOff, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1637244018403-785e7fa8707a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxjYW1lcm9vbiUyMHZpbGxhZ2UlMjBsYW5kc2NhcGUlMjBuYXR1cmUlMjBhZnJpY2F8ZW58MHx8fHwxNzkxMjIyMDY0fDA&ixlib=rb-4.1.0&q=85";

export const HomePage = () => {
  const { API } = useAuth();
  const { t, lang } = useLang();
  const [clans, setClans] = useState([]);
  const [stats, setStats] = useState({ total_persons: 0, contributors: 0, by_clan: {} });
  const [offlineReady, setOfflineReady] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(() => setOfflineReady(true)).catch(() => {});
    }
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cl, st] = await Promise.all([
          axios.get(`${API}/clans`),
          axios.get(`${API}/stats`),
        ]);
        setClans(cl.data);
        setStats(st.data);
      } catch (err) {
        console.error("HomePage: failed to load clans/stats:", err);
      }
    })();
  }, [API]);

  return (
    <div data-testid="home-page" className="pb-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Cameroon village" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(247,243,238,0.3) 0%, rgba(247,243,238,0.95) 95%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
          <div className="max-w-3xl fade-in-up">
            <div className="label-uppercase text-[#993D22] mb-4" data-testid="hero-eyebrow">
              Ekom 1 · Nkolafamba · Cameroun
            </div>
            <h1 className="font-serif-heritage text-5xl sm:text-6xl lg:text-7xl text-[#261911] leading-[1.02] font-semibold mb-6">
              {t("heroTitle")}
            </h1>
            <p className="text-lg text-[#3A2312] max-w-2xl mb-8">{t("heroSubtitle")}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/clans" className="btn-terra inline-flex items-center gap-2" data-testid="hero-explore-btn">
                {t("exploreClans")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/tree" className="btn-outline-terra inline-flex items-center gap-2" data-testid="hero-tree-btn">
                <Trees className="w-4 h-4" /> {t("viewTree")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 -mt-10 relative z-10">
        {(offlineReady || isOffline) && (
          <div
            className="mb-4 flex items-center gap-3 heritage-card px-5 py-3"
            data-testid="offline-ready-banner"
          >
            {isOffline ? (
              <>
                <WifiOff className="w-4 h-4 text-[#A96B18]" />
                <div>
                  <div className="label-uppercase text-[#A96B18]">Mode hors ligne actif</div>
                  <p className="text-xs text-[#6E5B4F]">
                    Vous consultez les données depuis le cache local. Les modifications attendront le retour du réseau.
                  </p>
                </div>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#2C4230]" />
                <div>
                  <div className="label-uppercase text-[#2C4230]">{t("offlineReady")}</div>
                  <p className="text-xs text-[#6E5B4F]">{t("offlineReadyDesc")}</p>
                </div>
              </>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          <div className="heritage-card p-6" data-testid="stat-persons">
            <div className="label-uppercase text-[#6E5B4F] mb-2">{t("totalPersons")}</div>
            <div className="font-serif-heritage text-5xl text-[#993D22]">{stats.total_persons}</div>
          </div>
          <div className="heritage-card p-6" data-testid="stat-verified">
            <div className="label-uppercase text-[#6E5B4F] mb-2">{t("cross_validated")}</div>
            <div className="font-serif-heritage text-5xl text-[#2C4230]">{stats.cross_validated ?? 0}</div>
            <div className="text-xs text-[#6E5B4F] mt-1">
              {stats.testimony_confirmed ?? 0} {t("testimony_confirmed").toLowerCase()} · {stats.to_verify ?? 0} {t("to_verify").toLowerCase()}
            </div>
          </div>
          <div className="heritage-card p-6" data-testid="stat-contributors">
            <div className="label-uppercase text-[#6E5B4F] mb-2">{t("contributors")}</div>
            <div className="font-serif-heritage text-5xl text-[#D9822B]">{stats.contributors}</div>
          </div>
          <div className="heritage-card p-6" data-testid="stat-clans">
            <div className="label-uppercase text-[#6E5B4F] mb-2">{t("clansCount")}</div>
            <div className="font-serif-heritage text-5xl text-[#6B4226]">{clans.length}</div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 mt-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="label-uppercase text-[#993D22] mb-3">{t("aboutTitle")}</div>
          <h2 className="font-serif-heritage text-4xl text-[#261911] mb-5">
            {lang === "fr" ? "Un village. Les grandes familles. Mille mémoires." : "One village. Great families. A thousand memories."}
          </h2>
        </div>
        <div className="md:col-span-7 text-[#3A2312] leading-relaxed text-base">
          {t("aboutText")}
          <div className="mt-6 p-5 rounded-xl border border-[#E3D5CA] bg-[#FAF7F2] flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#D9822B] mt-1 shrink-0" />
            <p className="text-sm text-[#6E5B4F]">
              {lang === "fr"
                ? "Chaque membre de la communauté peut contribuer : ajouter un parent, raconter une histoire, téléverser une photo. L'arbre grandit avec vous."
                : "Every community member can contribute: add a relative, share a story, upload a photo. The tree grows with you."}
            </p>
          </div>
        </div>
      </section>

      {/* CLANS PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 mt-20">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-uppercase text-[#993D22] mb-2">{t("clans")}</div>
            <h2 className="font-serif-heritage text-4xl text-[#261911]">
              {lang === "fr" ? "Les cinq familles Mvog Koé" : "The five Mvog Koé families"}
            </h2>
          </div>
          <Link to="/clans" className="text-[#993D22] text-sm font-semibold" data-testid="all-clans-link">
            {t("exploreClans")} →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {clans.map(c => (
            <Link
              to={`/clans/${c.key}`}
              key={c.key}
              className="heritage-card p-6 group"
              data-testid={`clan-card-${c.key}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-[#FAF7F2] font-serif-heritage text-2xl font-bold" style={{ background: c.color }}>
                  {c.name.split(" ").map(w => w[0]).join("").slice(0,2)}
                </div>
                <div className="flex-1">
                  <h3 className="font-serif-heritage text-2xl text-[#261911] mb-1">{c.name}</h3>
                  <p className="text-sm text-[#6E5B4F] line-clamp-2">{lang === "fr" ? c.description : c.description_en}</p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between text-xs">
                <span className="label-uppercase text-[#6E5B4F]">
                  <Users className="w-3 h-3 inline mr-1" /> {c.member_count} {t("members")}
                </span>
                <span className="text-[#993D22] font-semibold group-hover:translate-x-1 transition-transform">
                  {t("viewClan")} →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
